'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import LandingSearch from '@/new_components/shared/LandingSearch';
import { cn } from '@/lib/utils';
import { useLocale } from '@/hooks/use-locale';
import { useScrollPosition } from '@/hooks/use-scroll-position';
import { useSettings } from '@/providers/settings-provider';
import { Container } from '@/components/common/container';
import { HeaderLogo } from './header-logo';
import { HeaderMobile } from './header-mobile';
import { HeaderTopbar } from './header-topbar';
import { MegaMenu } from './mega-menu';

// The search's minimum width on the desktop row, plus a small buffer. The real
// search uses `min-w-[320px]`; the nav is floored at its content width
// (min-w-max), so at the switch point the search sits at this minimum and the
// nav at its natural width. Reserving it here keeps the probe's threshold in
// sync with that floor; the +24 buffer flips to the mobile header a hair before
// the nav items would sit edge-to-edge with the auth buttons. Raise the buffer
// to switch to mobile sooner.
const SEARCH_RESERVED_WIDTH = 320 + 24;

const Header = () => {
  const { settings } = useSettings();
  const scrollPosition = useScrollPosition();
  const [headerStickyOn, setHeaderStickyOn] = useState(false);
  // useLocale().internalPathname returns the delocalized path so /de/suche →
  // /search; matches the convention used in HeaderLogo and HeaderMobile.
  const { internalPathname } = useLocale();
  const isSearchPage = internalPathname === '/search';

  // Whether the desktop row fits in the available width, measured at two widths:
  //   full    — topbar with the Register/Login pills
  //   compact — topbar with the pills collapsed into the profile icon
  // `null` until the first client measurement, so SSR / first paint falls back
  // to the CSS `xl` breakpoint. As the viewport shrinks the header steps
  // pills → compact icon → mobile. See the two probes below (the LandingSearch
  // invisible-probe idiom applied to the header).
  const [fits, setFits] = useState<{ full: boolean; compact: boolean } | null>(
    null,
  );
  const availableRef = useRef<HTMLDivElement>(null);
  const probeFullRef = useRef<HTMLDivElement>(null);
  const probeCompactRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const isSticky = scrollPosition > settings.layouts.demo7.headerStickyOffset;
    setHeaderStickyOn(isSticky);
  }, [scrollPosition, settings]);

  useEffect(() => {
    if (headerStickyOn === true) {
      document.body.setAttribute('data-sticky-header', 'on');
    } else {
      document.body.removeAttribute('data-sticky-header');
    }
  }, [headerStickyOn]);

  // Decide mobile vs desktop by measurement rather than a fixed breakpoint: a
  // fixed px breakpoint can't know the real overlap point because the visible
  // top-level item count (Admin is permission-gated) and the label widths
  // (German labels run longer than English) both vary. The probe holds the row
  // at its natural, comfortable width with no flex-shrink; when that exceeds
  // the available width we switch to mobile. Both the available-width box and
  // the probe stay mounted in every mode so resizing back up can re-measure
  // (a display:none desktop row would latch us into mobile forever).
  useLayoutEffect(() => {
    const available = availableRef.current;
    const probeFull = probeFullRef.current;
    const probeCompact = probeCompactRef.current;
    if (!available || !probeFull || !probeCompact) return;
    const measure = () => {
      const w = available.clientWidth;
      setFits({
        full: probeFull.scrollWidth <= w,
        compact: probeCompact.scrollWidth <= w,
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(available);
    // Observing the probes re-measures when their natural width changes for
    // reasons other than viewport resize — language switch (DE labels), or
    // auth/menu changes (Admin item appears, login pills → avatar).
    ro.observe(probeFull);
    ro.observe(probeCompact);
    return () => ro.disconnect();
  }, [isSearchPage]);

  // Desktop shows as long as the compact layout fits; within desktop, the auth
  // collapses to the profile icon once the pills no longer fit.
  const showDesktop = fits === null ? null : fits.compact;
  const compactAuth = fits ? !fits.full : false;

  // Pre-measure (`null`) keeps the CSS `xl` breakpoint; once measured we drive
  // every layout decision off the boolean so the switch tracks real overlap.
  const headerLayout =
    showDesktop === null
      ? 'flex-col xl:flex-row xl:items-center xl:py-0 xl:h-(--header-height-default)'
      : showDesktop
        ? 'flex-row items-center py-0 h-(--header-height-default)'
        : 'flex-col';

  const mobileDisplay =
    showDesktop === null ? 'flex xl:hidden' : showDesktop ? 'hidden' : 'flex';

  const desktopDisplay =
    showDesktop === null ? 'hidden xl:flex' : showDesktop ? 'flex' : 'hidden';

  return (
    <header
      className={cn(
        // Mobile: two-row layout owned by HeaderMobile (flex-col). Desktop:
        // single row at the design's --header-height-default. Which one shows
        // is decided by the fit measurement above (with the `xl` breakpoint as
        // the pre-measurement fallback) rather than a hard breakpoint, so the
        // nav never crowds the search / auth controls.
        'fd-header sticky top-0 z-50 flex shrink-0 border-b w-full max-w-full transition-shadow',
        headerLayout,
        headerStickyOn &&
          'shadow-[0_1px_0_0_var(--fd-line-2),0_8px_24px_-16px_rgba(14,14,12,0.12)]',
      )}
      style={{
        borderColor: 'var(--fd-line)',
      }}
    >
      <HeaderMobile display={mobileDisplay} />

      {/* Always-mounted, zero-height measuring layer. The outer Container mirrors
          the real row's width budget (same fixed/fluid width + padding) so its
          clientWidth is the space the desktop row actually has; the inner probe
          holds that row at its natural, no-shrink width. Invisible + inert. */}
      <Container
        width={isSearchPage ? 'fluid' : undefined}
        className="absolute top-0 left-0 right-0 h-0 overflow-hidden invisible pointer-events-none"
      >
        <div
          ref={availableRef}
          aria-hidden="true"
          // `inert` fully removes the duplicated interactive components (nav
          // triggers, dropdown, links) from focus order and the a11y tree so
          // the probe is measurement-only.
          inert
          className="w-full"
        >
          {/* Two probe rows — identical except for the topbar's auth treatment.
              `full` carries the Register/Login pills, `compact` the collapsed
              profile icon. Their widths give the two switch thresholds. */}
          <div
            ref={probeFullRef}
            className="flex flex-nowrap items-center gap-6 w-max whitespace-nowrap"
          >
            <HeaderLogo />
            {/* The search at the width it actually holds on the desktop row. */}
            <div style={{ width: SEARCH_RESERVED_WIDTH }} className="shrink-0" />
            <nav className="flex items-stretch shrink-0">
              <MegaMenu />
            </nav>
            <HeaderTopbar stable />
          </div>
          <div
            ref={probeCompactRef}
            className="flex flex-nowrap items-center gap-6 w-max whitespace-nowrap"
          >
            <HeaderLogo />
            <div style={{ width: SEARCH_RESERVED_WIDTH }} className="shrink-0" />
            <nav className="flex items-stretch shrink-0">
              <MegaMenu />
            </nav>
            <HeaderTopbar stable compactAuth />
          </div>
        </div>
      </Container>

      {/* Logo is shrink-0 on the left, the search bar stretches to fill the
          space between the logo and the mega menu (flex-1 with a min-width
          floor so it stays usable), and the menu + topbar are a shrink-0 group
          on the right. On /search the container goes full-width (fluid) so the
          logo shifts to the page's left edge and the search bar expands into
          the freed space. Other pages keep the fixed 1320px container. */}
      <Container
        width={isSearchPage ? 'fluid' : undefined}
        className={cn(
          desktopDisplay,
          'flex-nowrap items-center gap-6 h-full min-w-0',
        )}
      >
        <HeaderLogo />

        {isSearchPage ? (
          <>
            {/* /search: the expanded search fills the row; menu + topbar stay
                a shrink-0 group on the right. */}
            <div className="flex-1 min-w-[260px] max-w-[1000px]">
              <LandingSearch compact className="max-w-none" />
            </div>
            <div className="flex items-center gap-6 shrink-0">
              <nav className="flex items-stretch shrink-0">
                <MegaMenu />
              </nav>
              <HeaderTopbar stable compactAuth={compactAuth} />
            </div>
          </>
        ) : (
          <>
            {/* Other pages: the search grows faster than the nav (flex-[2] vs
                flex-1) so it claims most of the leftover space, which keeps the
                nav region snug and the buttons from sprawling. The nav is
                floored at its content width (min-w-max) so it never clips —
                justify-between still spreads the buttons when there's spare room.
                `max-w-none` lets the search fill its grown wrapper (it otherwise
                self-caps at max-w-xl); the 320px floor matches the probe's
                reserved search width so the nav keeps its natural width until we
                switch to mobile. Topbar stays pinned to the right edge. */}
            <div className="flex-[2] min-w-[320px]">
              <LandingSearch compact className="max-w-none" />
            </div>
            <nav className="flex flex-1 items-stretch min-w-max">
              <MegaMenu fill />
            </nav>
            <HeaderTopbar stable compactAuth={compactAuth} />
          </>
        )}
      </Container>
    </header>
  );
};

export { Header };
